import React, { useEffect, useState, useCallback, useContext } from 'react'
import { DataGrid, NGODatePicker } from '../../../common';
import usePermissions from '../../../utils/userPermissions';
import { useTranslation } from 'react-i18next';
import { MdOutlineMailOutline } from "react-icons/md";
import moment from 'moment';
import {gridRowStyles, customSelSearch} from '../../../utils/configs';
import { getDateFromTimezone } from './Cards';
import { APIEndpoint } from '../../../utils/axios';
import { formatDateByTimezone, objectToQueryString } from '../../../utils/commonFunctions';
import { SectorRenderer, CardReconOrderStatusRenderSel } from "../../sales/inner-components/renderers";
import Select from "react-select";
import CustomSelect from '../../../common/CustomSelect.jsx';
import downloadMasterIcon from "../../../assets/icons/download-master.svg";
import closeIcon from '../../../images/icons/Icon2.png';
import LoadingGIF from '../../../assets/loading.gif';
import {ThemeContext} from "../../../contexts/themeContext";

const DATE_FORMAT = "DD-MM-YYYY";
const DATETIME_FORMAT = "DD-MM-YYYY hh:mm A";
const DOWNLOAD_STATUS_CHECK_DELAY = 8000;
const MAX_DATE_RANGE_DAYS = 30;
const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again after some time.";

let axiosAbortCtrl = [];
// axiosAbortCtrl["orderListAPI"] = new AbortController();

const newAbortSignal = (apiName, timeoutMs = 5000) => {

  axiosAbortCtrl[apiName] = new AbortController();
  // setTimeout(() => axiosAbortCtrl[apiName].abort(), timeoutMs || 0);

  return axiosAbortCtrl[apiName].signal;
};
const abortEarlierRequest = (apiName = "") => {
    if (apiName === "" || typeof axiosAbortCtrl[apiName] === "undefined") return;

    axiosAbortCtrl[apiName].abort(
        apiName + ": Cancelling API running earlier..."
    );
};

const body = {
  page:1,
  filters:[],
  limit:50
}
const orderStatusStyle = { fontWeight:"400",fontSize:"0.8rem" };
const Columns = [
    {field: "flightDate",headerName: "Flight Date",flex:1,minWidth:100, cellStyle:gridRowStyles,resizable:true,valueGetter: params => {
        return params.data && params.data?.flightDate ? moment(params.data?.flightDate).format(DATE_FORMAT) : '';
    }},
    {field: "txnDate",headerName: "Txn Date Time",flex:1,minWidth:150,cellStyle:gridRowStyles,resizable:true,valueGetter: params => {
        return params.data && params.data?.txnDate ? moment(params.data?.txnDate).format(DATETIME_FORMAT) : '';
    }},
    { field: 'storeName', headerName: 'Sector',resizable:true,flex:1, minWidth: 165, width:170,maxWidth:250,cellRendererSelector: (params) => {
        return {
        component: SectorRenderer,
        };
    }},
    {field: "orderNo",headerName: "Order No",flex:1,minWidth:235,cellStyle:gridRowStyles,resizable:true, 
        cellClass: 'long-text-cell'
    },
    {field: "orderStatus",headerName: "Order Status",sortable:true,filter: true,
        minWidth: 140, tooltipField: 'orderStatus', autoHeight: true,
        cellRendererSelector: (params) => {
            return {
            component: CardReconOrderStatusRenderSel
            }
        }
    },
    { field: "crewName", headerName: "Crew",flex:1, minWidth:150, cellStyle: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow:'ellipsis',
    },resizable:true },
    {field: "passengerName",headerName: "Passenger",flex:1,minWidth:250,cellStyle: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow:'ellipsis',
        }, resizable:true
    },
    {field: "pnr",headerName: "PNR",flex:1,minWidth:100,cellStyle: gridRowStyles,resizable:true},
    {field: "deviceId",headerName: "Device ID",flex: 1,minWidth:140,cellStyle: gridRowStyles,resizable:true},
    {field: "cardNo",headerName: "Card No",flex:1,minWidth:130,cellStyle: gridRowStyles,resizable:true},
    {field: "amount",headerName: "Sales Amount",flex:1,minWidth:140,cellStyle: gridRowStyles,resizable:true},
    {field: "amountCredited",headerName: "Txn Amount",flex:1,minWidth:130, cellStyle: gridRowStyles, resizable:true},
    {field: "baseAmount",headerName: "Base Amount",flex:1,minWidth:130, cellStyle: gridRowStyles, resizable:true, valueGetter: params => {
        return params.data ? (params.data.baseAmount != null ? params.data.baseCurrency + ' ' + params.data.baseAmount : '') : '';
    }},
    {field: "syncDateTime",headerName: "Sync Date Time",flex:1,minWidth:150,cellStyle:gridRowStyles,resizable:true,valueGetter: params => {
        return params.data && params.data?.syncDateTime ? moment(params.data.syncDateTime).format(DATETIME_FORMAT) : '';
    }},
    {field: "comment", headerName: "Reason (nGO)", flex: 1, minWidth:140, cellStyle: gridRowStyles, resizable:true, tooltipField: 'comment'},
    {field: "paymentStatus",headerName: "Payment Status",flex:1,minWidth:140},
    {field: "bankTxnId",headerName: "Bank Ref No",flex:1,minWidth:140,cellStyle: gridRowStyles,resizable:true},
    {field: "reasonBank",headerName: "Reason (Bank)",flex: 1,minWidth:140,cellStyle: gridRowStyles,resizable:true, tooltipField: 'reasonBank'},
  { field: 'actions',pinned:'right',autoResize:true,headerName:"",width:60, cellRenderer: ActionComponent },
];

function ActionComponent (props) {

  const {data} = props;
  const handleEmailClick = () => {
    const subject = `Payment Collection - ${data?.orderNo}`;
    const body = ``;
    const mailtoLink = `mailto:customer@akasaair.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoLink;
  };

  return (<>
    {data!== undefined? <MdOutlineMailOutline title='Send Mail' color='#fff' size={20} style={{cursor:"pointer"}} onClick={handleEmailClick}/>:<></>}
    </>
  );
};

export const CardReconciliation = () => {
    const { hasPermission: viewCardRecon } = usePermissions('view', "card-reconciliation");
    const { hasPermission: downloadCardRecon } = usePermissions('download', "card-reconciliation");
    const [perPageLimit, ] = useState(50);
    const [noResultsMessage, setNoResultsMessage] = useState('No Data');
    const {themedColor} = useContext(ThemeContext);

    const [sectorList, setSectorsList] = useState([]);
    const [sectorPageObj, setSectorPageObj] = useState({
        page: 1,
        size: 2000,
        q: "",
    });

    const [filterData, setFilterData] = useState({
        flightDate: "",
        flightNumber: "",
        sectorVal: "",
        pnrNumber: "",
        orderNumber: "",
        crewCode: "",
        orderStatus: ""
    });
    const [isReloadTable, setIsReloadTable] = useState(false)
    const { t, ready } = useTranslation();
    const [ crewMembers, setCrewMembers ] = useState([]);
    const [state, setState] = useState({ isDownloadReportError: false, message: null });
    const [showDownload, setShowDownload]=useState(false);
    const [reportData, setReportData]=useState({ startDate: "", endDate: "" });
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        getSectorList();
        getCrewMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSectorList = useCallback( () => {
        try {
            APIEndpoint.get("stores/byPage?" + objectToQueryString(sectorPageObj)).then(
                (resp) => {
                    setSectorPageObj({ ...sectorPageObj, page: sectorPageObj.page + 1 });
                    const list = [];
                    if (resp.result.length > 0) {
                        resp.result.forEach((sector) => {
                            list.push({ value: sector.id, id: sector.id, label: sector.value });
                        });
            
                        setSectorsList(list);
                    }
                }
            ).catch( err => {
                console.log("Log: Error while fetching stores", err);
            });
        }
        catch (error) {
            console.log("Log: Error while fetching stores", error);
        }
        return false;
    }, [sectorPageObj]);

    const getCrewMembers = useCallback( async () => {
        try {
            APIEndpoint.get("sandboxing/employees?includeAdmin=no").then(
                (resp) => {
                    const list = [{ key:0, label: 'Select Crew', value: "" }];
                    if (resp && resp.result.length > 0) {
                        resp.result.forEach((crew) => {
                            list.push({ key: crew.employeeId, value: crew.employeeCode, label: `${crew.employeeName} (${crew.employeeCode})`, employeeId: crew.employeeId});
                        });

                        setCrewMembers(list);
                    }
                }
            ).catch( err => {
                console.log("Log: Error while fetching crew members", err);
            });
        }
        catch (error) {
            console.log("Log: Error while fetching crew members", error);
        }
        return false;
    }, []);

    useEffect(() => {
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ filterData.flightDate ]);
    useEffect(() => {
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ filterData.flightNumber ]);
    useEffect(() => {
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ filterData.sectorVal ]);
    useEffect(() => {
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ filterData.pnrNumber ]);
    useEffect(() => {
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ filterData.orderNumber ]);
    useEffect(() => {
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ filterData.crewCode ]);

    useEffect(() => {
        setIsReloadTable(prev => !prev);
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ filterData.orderStatus ]);
    
    const fetchCardTransactions = async (page = 1, perPage = 50) => {

        if(!viewCardRecon) {
            return { items: [], count: 0 }
        }

        body.filters = [];
        body.page = page;
        body.limit = perPage;
        if(filterData.flightNumber) {
          body.filters.push({flightNumber:filterData.flightNumber});
        }
        if(filterData.sectorVal) {
          body.filters.push({ storeId: filterData.sectorVal?.value});
        }
        if(filterData.orderNumber) {
          body.filters.push({orderNo: filterData.orderNumber});
        }
        if(filterData.crewCode){
          body.filters.push({crewCode: filterData.crewCode});
        }
        if(filterData.pnrNumber) {
          body.filters.push({pnr: filterData.pnrNumber});
        }
        if(filterData.flightDate) {
          body.filters.push({flightDate: formatDateByTimezone(filterData.flightDate)});
        }
        if(filterData.orderStatus) {
          body.filters.push({orderStatus:filterData.orderStatus});
        }

        try {
          const res = await APIEndpoint.post("payments/card/transactions",JSON.stringify(body), { signal: newAbortSignal('cardTransactionsAPI', 5000) })
            if(res && res.items && res.items.length>0 && viewCardRecon){
                return res;
            } else if(res.items && res.items.length===0){
                setNoResultsMessage('No Data');
            }
        } catch (error) {
            console.log('Log: Error while fetching data', error);
        }
    };

    function isValidDate(dateString) {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    }
    
    const handleDateChange = (e) => {
        let { name, value } = e.target;
        if (!value) {
            setFilterData(prev=>({...prev, flightDate: null}));
            return;
        }
        if (name === "flightDate") {
            if(!isValidDate(value)) return;

            let dataVal = getDateFromTimezone(value).toDate();
            setFilterData((prev) => {
                return { ...prev, flightDate: dataVal };
            });
            abortEarlierRequest("cardTransactionsAPI");
        }
    };

    const handleSelectChange = (e) => {
        let { name, value } = e.target;
        if (name === "orderStatus") {
          setFilterData((prev) => {
            return { ...prev, orderStatus: value };
          });
          abortEarlierRequest("cardTransactionsAPI");
        }
    };
    function onSearchInpChange(e){
        let { name, value } = e.target;
        setFilterData(prev=>({...prev, [name]: value}));
        abortEarlierRequest("cardTransactionsAPI");
    }
    const onSectorChange = (e) => {
        setFilterData( (prev) => ({ ...prev, sectorVal: e }) );
        abortEarlierRequest("cardTransactionsAPI");
    };
    
    const onCrewFilterChange=(e)=>{
        setFilterData( (prev) => ({ ...prev, crewCode: e?.value || "" }) );
        abortEarlierRequest("cardTransactionsAPI");
    }

    const checkDownloadStatus = async (downloadId) => {
        let statusCheckTimeout=null;
        setIsDownloading(true);

        await APIEndpoint.get('downloads/status/'+ downloadId)
        .then( resp => {
            if (resp.status === 'completed') {
                setTimeout(() => {
                    const downloadStatusPromise= APIEndpoint.get(`downloads/status/${downloadId}`);
                    downloadStatusPromise.then((val) => {
                        if (val.status === "completed") {
                            APIEndpoint.get(`downloads/analytics/${val.fileName}${val.fileExtension}`)
                            .then((userAccountDownload) => {
                                const url = window.URL.createObjectURL(new Blob([userAccountDownload]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `${val.fileName}${val.fileExtension}`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);

                                handleCloseDownloadReport();
                            })
                            .catch((error) => {
                                console.error('Log: Error downloading CSV:', error);
                            });
                            
                            setIsDownloading(false);
                        }
                        else if (val.status === "failed") {
                            setIsDownloading(false);

                            setState((_) => {
                                return { ..._, isDownloadReportError: true,
                                    message: {
                                        text: val?.data?.message || DEFAULT_ERROR_MESSAGE
                                    },
                                };
                            });
                        }
                    }).catch( error => {
                        console.error('Log: Error while checking download status:', error)
                        setIsDownloading(false);
                    });

                }, DOWNLOAD_STATUS_CHECK_DELAY);

            }
            else {
                clearTimeout(statusCheckTimeout);
                statusCheckTimeout = setTimeout(() => {
                    checkDownloadStatus(downloadId);
                }, 5000);
            }

        }).catch( error => {
            console.error('Log: Error while checking download status:', error);
            setIsDownloading(false);
            try {
                const errorTxt = JSON.parse( error?.message );
                setState((_) => {
                    return { ..._, isDownloadReportError: true,
                        message: { 
                            text: errorTxt?.data?.error?.title || DEFAULT_ERROR_MESSAGE
                        }
                    };
                });

            } catch (err) {
                console.error('Log: Error parsing error message:', err);
                setState((_) => {
                    return { ..._, isDownloadReportError: true, 
                        message: {
                            text: err?.data?.message || DEFAULT_ERROR_MESSAGE
                        }
                    };
                });
            }
        });

        
    };

    const onDownloadCardReconciliationReport = async (e)=> {
        setState(prev => ({ ...prev, isDownloadReportError: false, message: { text: "" } }));

        const headers = {
            'Accept':'text/csv'
        };
        
        if (!reportData.startDate || !reportData.endDate) {
            setState((_) => {
                return { ..._, isDownloadReportError: true, 
                    message: {
                        text: "Please select both start date and end date."
                    }
                };
            });
            return;
        }

        const filters = {
            fromFlightDate: reportData.startDate ? formatDateByTimezone(reportData.startDate) : "",
            toFlightDate: reportData.endDate ? formatDateByTimezone(reportData.endDate) : ""
        };

        try {
            setIsDownloading(true);
      
            const res= await APIEndpoint.post(`downloads/cardReconciliationReport`, { 
                ...filters
            }, headers);
    
            if(res.status==='scheduled') {
                checkDownloadStatus(res.id);
            }
        }
        catch (error) {
            setIsDownloading(false);
            console.error('Log: Error while downloading:', error);
            try {
                const errorTxt = JSON.parse( error?.message );
                setState((_) => {
                    return { ..._, isDownloadReportError: true,
                        message: {
                            text: error?.data?.message || errorTxt?.data?.message || DEFAULT_ERROR_MESSAGE
                        }
                    };
                });

            } catch (err) {
                console.error('Log: Error parsing error message:', err);
                setState((_) => {
                    return { ..._, isDownloadReportError: true, 
                        message: { 
                            text: err?.data?.message || DEFAULT_ERROR_MESSAGE
                        }
                    };
                });
            }
        }
    };

    const onReportDateChange = (e) => {
        const { name, value } = e.target;
        setReportData((prev) => ({ ...prev, [name]: value }));
    }

    const getEndDateMaxDate = (startDate) => {
        if (!startDate) {
            return new Date();
        }
        const maxDate = new Date(startDate).getTime() + MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000;
        const today = new Date().getTime();
        return maxDate > today ? new Date() : new Date(maxDate);
    };

    const handleCloseDownloadReport = () => {
        setShowDownload(false);
        setReportData({ startDate: "", endDate: "" });
        setState(prev => ({ ...prev, isDownloadReportError: false, message: { text: "" } }));
    }

    const onClickDownloadReport = async (e) => {
        handleCloseDownloadReport();
        setShowDownload(true);
    };

    return (
        <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
        { viewCardRecon ?
        <>
            <div
                className="flexRow flexWrap justifyContentFlexStart alignItemsCenter"
            >
                <div className='flexCol m-2'>
                    <NGODatePicker
                    value={filterData.flightDate}
                    name='flightDate'
                    onChange={handleDateChange}
                    placeholder="Flight Date"
                    isClearable={true}
                    />
                </div>
                <div
                    className="orderBy-div m-2 margLeft"
                    style={{ marginBottom: "0.3rem", marginLeft: "1rem" }}
                >
                    <div className='orderBy-div m-2 margLeft'>
                        <Select
                            name="sector"
                            options={sectorList}
                            defaultValue={
                                filterData?.sectorVal?.label ? filterData.sectorVal.label : ""
                            }
                            value={ sectorList?.filter(function (option) {
                                return (filterData.sectorVal && filterData.sectorVal.label === option.label)
                            })}
                            onChange={onSectorChange}
                            styles={customSelSearch(themedColor)}
                            placeholder="Search Sectors"
                            isSearchable
                            isClearable
                            className="selectMenu-meals"
                        />
                    </div>
                </div>
                <div className="flexCol m-2">
                    <div className='operations-in'>
                        <input
                            name='orderNumber'
                            className="search-crew searchReconFilter"
                            onChange={onSearchInpChange}
                            type="search"
                            placeholder="Search Order Number"
                            value={filterData.orderNumber || null}
                        />
                    </div>
                </div>
                <div className="flexCol m-2">
                    <div className='operations-in'>
                        <input
                            name='pnrNumber'
                            className="search-crew searchReconFilter"
                            onChange={onSearchInpChange}
                            type="search"
                            placeholder="Search PNR Number"
                            value={filterData.pnrNumber || null}
                        />
                    </div>
                </div>
                <div className="orderBy-div m-2 margLeft" style={{ marginBottom: "0.3rem" }}>
                    <div className='orderBy-div m-0 margLeft'>
                        <button className="" style={{ padding: 0, border: "none", background: "none" }}>
                            <CustomSelect
                                styles={customSelSearch(themedColor)}
                                onChange={onCrewFilterChange}
                                options={crewMembers}
                                placeHolder={"Search Crew"}
                                width={"15rem"}
                                value={ crewMembers?.filter(function (option) {
                                    return (
                                        option.value === filterData.crewCode
                                    );
                                })}
                            />
                        </button>
                    </div>
                </div>
                <div className="orderBy-div m-2 margLeft" style={{ marginBottom: "0.3rem" }}>
                    <div className='orderBy-div m-0 margLeft'>
                        
                        <select 
                            onChange={handleSelectChange} 
                            style={ orderStatusStyle } name="orderStatus" className='select-item darkBlue-bg' id="orderStatus">
                            <option value="">Order Status</option>
                            <option value="accepted">Accepted</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="denied">Denied Demand</option>
                        </select>
                    </div>
                </div>
                <div className="flex flexRow alignItemsCenter justifyContentFlexStart flexWrap">
                    <div className='flexCol m-2'>
                        { downloadCardRecon ? (
                                <>
                                    <button 
                                        className='dropdownStyle1-TextMedium'
                                        style={{marginLeft: "auto", marginRight: "0.5rem"}}
                                        disabled={isDownloading}
                                        onClick={ (e) => onClickDownloadReport(e)}
                                        title={"Download Card Reconciliation Report"}>
                                            <img src={downloadMasterIcon} alt='Download' className='icon-size-min mt-1' title={"Download Card Reconciliation Report"} />
                                    </button>
                                </>
                            ) : null
                        }
                    </div>
                </div>
            </div>
        
            <DataGrid
                className="full-flex"
                columns={[...Columns]}
                initialPageLimit={ perPageLimit }
                showDefaultFilters={false}
                getRows={ fetchCardTransactions }
                columnResize={ true }
                rowSelection="multiple"
                resetDataSource={isReloadTable}
                noResultsMessage={noResultsMessage}
            />
        </>:
            null
        }

        {showDownload &&
            <div className="overlay" role="dialog">
                <div className="dialog sm-scrollbar">
                    <div className="dialog__content">
                        <button
                            type="button"
                            className="modal__close_icon"
                            onClick={handleCloseDownloadReport}
                            aria-label="Close"
                            tabIndex={0}
                        >
                            <img src={closeIcon} alt="Close" />
                        </button>

                        <h2 className="dialog__title subheading-text-medium fontMedium">Download Card Reconciliation Report</h2>
                        <hr />

                        <div className="dialog__body_container flexRow mt-4 button-align justifyContentCenter flexWrap">
                            <div className={`dialog__container_item input-container`}>
                                <div className={`dialog__description`}>
                                    
                                    <div className='flexCol justifyContentSpaceBetween'>
                                        
                                        <div className='flexCol justifyContentCenter  m-2'>
                                            <label className="textAlignLeft fontRegular req" htmlFor="startDate">{t('Start Date')}</label>
                                            <NGODatePicker
                                                value={reportData.startDate}
                                                name='startDate'
                                                onChange={ onReportDateChange }
                                                placeholder={t('Start Date')}
                                                placeholdertext={t('datePickerPlaceholder')}
                                                minDate={reportData.endDate ? 
                                                    new Date(new Date(reportData.endDate).getTime() - MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000) 
                                                    : null
                                                }
                                                maxDate={reportData.endDate ? new Date(reportData.endDate) : new Date()}
                                                isClearable={ true }
                                                tabIndex={1}
                                            />
                                        </div>
                                        <div className='flexCol justifyContentCenter m-2'>
                                            <label className="textAlignLeft fontRegular req" htmlFor="endDate">{t('End Date')}</label>
                                            <NGODatePicker
                                                value={reportData.endDate}
                                                name='endDate'
                                                onChange={ onReportDateChange }
                                                placeholder={t('End Date')}
                                                placeholdertext={t('datePickerPlaceholder')}
                                                minDate={reportData.startDate ? new Date(reportData.startDate) : null}
                                                maxDate={ getEndDateMaxDate(reportData.startDate) }
                                                isClearable={ true }
                                                tabIndex={2}
                                            />
                                        </div>

                                        {state.isDownloadReportError ? 
                                            <div className='flex justifyContentCenter alignItemsCenter m-2'>
                                                <div className={ "dialog__statusBlock " + (state.isDownloadReportError ? 'alert_error' : 'alert_success') }>
                                                    {state.message?.text}
                                                </div>
                                            </div>
                                        : null }
                                        
                                        <div className='flexRow justifyContentCenter alignItemsCenter m-2'>
                                            <button type="button"
                                                onClick={ (e) => onDownloadCardReconciliationReport(e) } 
                                                disabled={ isDownloading || !reportData.startDate || !reportData.endDate } 
                                                className="btn btn-info mt-4" style={{ color: "white", width: "100%" }}>
                                                { isDownloading ? 
                                                    <div className='flexRow justifyContentCenter alignItemsCenter'>
                                                        <img src={LoadingGIF} className='icon-size-min' alt="Loading" /> Processing...
                                                    </div>
                                                    :
                                                    <span><span><img className="icon-size mt-1" alt='downloadMaster' src={downloadMasterIcon} /></span> Download</span>
                                                }
                                            </button>
                                        </div>

                                    </div>
                                    
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        }
        {
            !ready &&<div className='overlay_hide sub'></div>
        }
        </div>
    );
};